import {
  Box,
  Modal,
  SxProps,
  Typography,
  Theme,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JobExperience, JobExperienceRequest } from '../../types/jobExperience';
import { JobPosition } from '../../types/job-position';
import { JobAPI } from '../../services/job-service/api';
import { ExperienceCompany } from '../../types/experienceCompany';
import moment from 'moment';
import { SearchAPI } from '../../services/search-service/api';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { UserAPI } from '../../services/user/api';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const modalStyle: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: {
    xs: '90vw',
    sm: '50vw',
  },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  borderColor: 'primary.main',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 10,
};

type JobPositionOption = {
  id: number;
  label: string;
};

const JobExperienceModal = ({
  modalOpen,
  setModalOpen,
  jobExperiences,
  setJobExperiences,
  jobExperience,
  setJobExperience,
  api,
}: {
  modalOpen: boolean;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  jobExperiences: JobExperience[];
  setJobExperiences: Dispatch<SetStateAction<JobExperience[]>>;
  jobExperience: JobExperience | undefined;
  setJobExperience: Dispatch<SetStateAction<JobExperience | undefined>>;
  api: UserAPI;
}) => {
  const { t } = useTranslation();
  const [duration, setDuration] = useState('');
  const [isLast, setIsLast] = useState(false);
  const [positionId, setPositionId] = useState(0);
  const [jobPositions, setJobPositions] = useState<JobPositionOption[]>([]);
  const [selectedJobPosition, setSelectedJobPosition] = useState<JobPositionOption | undefined>(
    undefined,
  );
  const [jobPositionSearchQuery, setJobPositionSearchQuery] = useState('');
  const debouncedJobPositionQuery = useDebounce(jobPositionSearchQuery, 500);
  const [jobPositionLoading, setJobPositionLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [experienceCompanies, setExperienceCompanies] = useState<ExperienceCompany[]>([]);
  const [selectedExperienceCompany, setSelectedExperienceCompany] = useState<
    ExperienceCompany | undefined
  >(undefined);
  const [experienceCompanySearchQuery, setExperienceCompanySearchQuery] = useState('');
  const debouncedExperienceCompanyQuery = useDebounce(experienceCompanySearchQuery, 500);
  const [experienceCompanyInputValue, setExperienceCompanyInputValue] = useState('');
  const [experienceCompanyLoading, setExperienceCompanyLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>(''); // YYYY
  const [endDate, setEndDate] = useState<string>(''); // YYYY
  const [companyName, setCompanyName] = useState('');
  const [isVisibleAddExperienceCompany, setIsVisibleAddExperienceCompany] = useState(false);
  const [addExperienceCompanyLoading, setAddExperienceCompanyLoading] = useState(false);
  const bondedJobAPI = new JobAPI();
  const bondedSearchAPI = new SearchAPI();

  const getJobPositions = useCallback(async () => {
    setJobPositionLoading(true);
    const jobPositionsData = await bondedJobAPI.getJobPositions(debouncedJobPositionQuery);
    const mappedJobPositions = jobPositionsData.map((jobPosition) =>
      jobPositionMapper(jobPosition),
    );
    setJobPositions(mappedJobPositions);
    setJobPositionLoading(false);
  }, [debouncedJobPositionQuery]);

  const getExperienceCompanies = useCallback(async () => {
    if (debouncedExperienceCompanyQuery === '') {
      setExperienceCompanies([]);
      setExperienceCompanyLoading(false);
      return;
    }

    setExperienceCompanyLoading(true);
    const experienceCompaniesData = await bondedSearchAPI.getExperienceCompanies(
      debouncedExperienceCompanyQuery,
    );
    setExperienceCompanies(experienceCompaniesData);
    setExperienceCompanyLoading(false);
  }, [debouncedExperienceCompanyQuery]);

  const jobPositionMapper = (jobPosition: JobPosition): JobPositionOption => {
    return {
      id: jobPosition.id,
      label: t(jobPosition.translateKey),
    };
  };

  const onJobPositionChange = (jobPositionInput: JobPositionOption | null) => {
    if (jobPositionInput) {
      setSelectedJobPosition(jobPositionInput);
      setPositionId(jobPositionInput.id);
      return;
    }

    setSelectedJobPosition(undefined);
    setPositionId(0);
  };

  const onExperienceCompanyChange = (experienceCompanyInput: ExperienceCompany | null) => {
    if (experienceCompanyInput) {
      setSelectedExperienceCompany(experienceCompanyInput);
      setExperienceCompanySearchQuery(experienceCompanyInput.companyName);
      return;
    }

    setSelectedExperienceCompany(undefined);
    setExperienceCompanySearchQuery('');
  };

  const addExperienceCompany = async () => {
    if (companyName === '') {
      setError('jobExperience.companyNameRequired');
      return;
    }
    try {
      setAddExperienceCompanyLoading(true);

      const experienceCompanyResponse = await api.addExperienceCompany(companyName);
      setExperienceCompanies([
        {
          id: experienceCompanyResponse.id,
          companyName: experienceCompanyResponse.companyName,
        },
      ]);
      setSelectedExperienceCompany({
        id: experienceCompanyResponse.id,
        companyName: experienceCompanyResponse.companyName,
      });

      setExperienceCompanyInputValue(experienceCompanyResponse.companyName);
      setExperienceCompanySearchQuery(experienceCompanyResponse.companyName);
      setIsVisibleAddExperienceCompany(false);
      setCompanyName('');
      setError('');
    } catch (e) {
      setError('common.somethingWentWrong');
    } finally {
      setAddExperienceCompanyLoading(false);
    }
  };

  const validateDates = () => {
    if (startDate && endDate) {
      const startYear = parseInt(startDate, 10);
      const endYear = parseInt(endDate, 10);

      if (startYear > endYear) {
        setError('jobExperience.startDateAfterEndDate');
        return false;
      }

      const currentYear = parseInt(moment().format('YYYY'), 10);
      if (endYear > currentYear) {
        setError('jobExperience.endDateInFuture');
        return false;
      }
    }
    return true;
  };

  const onSubmit = async () => {
    setLoading(true);
    setError('');

    if (
      positionId === 0 ||
      duration === '' ||
      inputValue === '' ||
      selectedExperienceCompany === undefined ||
      selectedJobPosition === undefined
    ) {
      setError('jobExperience.allFieldsRequired');
      setLoading(false);
      return;
    }

    if (!validateDates()) {
      setLoading(false);
      return;
    }

    const jobExperienceRequest: JobExperienceRequest = {
      experienceCompanyId: selectedExperienceCompany.id,
      jobPositionId: positionId,
      duration: +duration,
      isLast: isLast,
      startDate: startDate,
      endDate: endDate,
    };

    if (jobExperience) {
      try {
        const jobExperienceResponse = await api.updateExperience(
          jobExperience.id,
          jobExperienceRequest,
        );
        const updatedJobExperiencesWithoutResponse = jobExperiences.filter(
          (jobExperienceElement) => jobExperienceElement.id !== jobExperienceResponse.id,
        );
        setJobExperiences([...updatedJobExperiencesWithoutResponse, jobExperienceResponse]);
        setError('');
        setSuccess('jobExperience.jobExperienceUpdated');
        setLoading(false);
      } catch (e) {
        setError('common.somethingWentWrong');
        setSuccess('');
        setLoading(false);
      }
      return;
    }

    try {
      const jobExperienceResponse = await api.addExperience(jobExperienceRequest);
      setJobExperiences([...jobExperiences, jobExperienceResponse]);
      setError('');
      setSuccess('jobExperience.jobExperienceAdded');
      setLoading(false);
    } catch (e) {
      setError('common.somethingWentWrong');
      setSuccess('');
      setLoading(false);
    }
  };

  const onDelete = async () => {
    setLoading(true);
    if (!jobExperience) {
      setError('jobExperience.jobExperienceIsNotExist');
      setSuccess('');
      setLoading(false);
      return;
    }
    try {
      await api.deleteExperience(jobExperience.id);
      const updatedJobExperiences = jobExperiences.filter(
        (jobExperienceElement) => jobExperienceElement.id !== jobExperience.id,
      );
      setJobExperiences(updatedJobExperiences);
      setJobExperience(undefined);
      setLoading(false);
      onModalClose();
    } catch (e) {
      setError('common.somethingWentWrong');
      setSuccess('');
      setLoading(false);
    }
  };

  const onModalClose = () => {
    resetModalValues();
    setModalOpen(false);
  };

  const resetModalValues = () => {
    setSelectedJobPosition(undefined);
    setSelectedExperienceCompany(undefined);
    setPositionId(0);
    setDuration('');
    setIsLast(false);
    setStartDate('');
    setEndDate('');
    setJobPositionSearchQuery('');
    setExperienceCompanySearchQuery('');
    setInputValue('');
    setExperienceCompanyInputValue('');
    setError('');
    setSuccess('');
    setIsVisibleAddExperienceCompany(false);
  };

  useEffect(() => {
    getJobPositions();
  }, [debouncedJobPositionQuery, getJobPositions]);

  useEffect(() => {
    getExperienceCompanies();
  }, [debouncedExperienceCompanyQuery, getExperienceCompanies]);

  useEffect(() => {
    if (modalOpen && jobExperience && jobExperience.jobPosition) {
      const mappedJobPosition = jobPositionMapper(jobExperience.jobPosition);
      setSelectedJobPosition(mappedJobPosition);
      setPositionId(jobExperience.jobPosition.id);
      setInputValue(
        jobExperience.jobPosition.translateKey
          ? (t(jobExperience.jobPosition.translateKey) as string)
          : '',
      );
      setJobPositionSearchQuery(
        jobExperience.jobPosition.translateKey
          ? (t(jobExperience.jobPosition.translateKey) as string)
          : '',
      );

      setSelectedExperienceCompany(jobExperience.company);
      setExperienceCompanyInputValue(jobExperience.company?.companyName || '');
      setExperienceCompanySearchQuery(jobExperience.company?.companyName || '');

      setDuration(jobExperience.duration.toString());
      setIsLast(jobExperience.isLast);
      setStartDate(jobExperience.startDate || '');
      setEndDate(jobExperience.endDate || '');
    }
  }, [jobExperience, modalOpen]);

  return (
    <Modal
      open={modalOpen}
      onClose={() => onModalClose()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={modalStyle}>
        <Typography id="modal-modal-title" variant="h6" component="h2">
          {jobExperience
            ? t('jobExperience.editJobExperience')
            : t('jobExperience.addJobExperience')}
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{ width: '80%' }}>
          {error && <Alert severity="error">{t(error)}</Alert>}
          {success && <Alert severity="success">{t(success)}</Alert>}

          <Autocomplete
            sx={{ marginY: '1rem' }}
            id="combo-box-experience-companies"
            filterOptions={(x) => x}
            options={experienceCompanies}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
            fullWidth
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.companyName)}
            isOptionEqualToValue={(option, value) => option.companyName === value.companyName}
            value={selectedExperienceCompany}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                return;
              }
              onExperienceCompanyChange(newValue);
            }}
            inputValue={experienceCompanyInputValue}
            onInputChange={(event, newInputValue) => {
              setExperienceCompanySearchQuery(newInputValue);
              setExperienceCompanyInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t('jobExperience.experienceCompanies')} />
            )}
            loading={experienceCompanyLoading}
            loadingText={t('jobExperience.loading')}
          />
          {isVisibleAddExperienceCompany ? (
            <Box display="flex" flexDirection="row" gap="0.5rem">
              <TextField
                fullWidth
                label={t('jobExperience.companyName')}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
              <Button
                onClick={() => addExperienceCompany()}
                disabled={addExperienceCompanyLoading}
                size="small"
                variant="contained"
                color="success">
                {addExperienceCompanyLoading ? <CircularProgress /> : <CheckCircleOutline />}
              </Button>
              <Button
                onClick={() => {
                  setIsVisibleAddExperienceCompany(false);
                  setCompanyName('');
                  setError('');
                }}
                disabled={addExperienceCompanyLoading}
                size="small"
                variant="contained"
                color="error">
                <CancelOutlined />
              </Button>
            </Box>
          ) : (
            <Button onClick={() => setIsVisibleAddExperienceCompany(true)}>
              {t('jobExperience.addExperienceCompany')}
            </Button>
          )}

          <Autocomplete
            sx={{ marginTop: '1rem' }}
            id="combo-box-job-positions"
            filterOptions={(x) => x}
            options={jobPositions}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
            fullWidth
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            isOptionEqualToValue={(option, value) => option.label === value.label}
            value={selectedJobPosition}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                return;
              }
              onJobPositionChange(newValue);
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setJobPositionSearchQuery(newInputValue);
              setInputValue(newInputValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label={t('jobExperience.jobPositions')} />
            )}
            loading={jobPositionLoading}
            loadingText={t('jobExperience.loading')}
          />
          <TextField
            fullWidth
            sx={{ marginTop: '2rem' }}
            label={t('jobExperience.startDate')}
            value={startDate}
            onChange={(e) => {
              const value = e.target.value;
              // Only accept 4-digit year format (YYYY)
              if (value === '' || (/^\d{0,4}$/.test(value) && value.length <= 4)) {
                setStartDate(value);
              }
            }}
            type="text"
            inputProps={{
              maxLength: 4,
              pattern: '[0-9]{4}',
              placeholder: moment().format('YYYY'),
            }}
          />
          <TextField
            fullWidth
            sx={{ marginTop: '2rem' }}
            label={t('jobExperience.endDate')}
            value={endDate}
            onChange={(e) => {
              const value = e.target.value;
              // Only accept 4-digit year format (YYYY)
              if (value === '' || (/^\d{0,4}$/.test(value) && value.length <= 4)) {
                setEndDate(value);
              }
            }}
            type="text"
            inputProps={{
              maxLength: 4,
              pattern: '[0-9]{4}',
              placeholder: moment().format('YYYY'),
            }}
          />
          <TextField
            fullWidth
            sx={{ marginY: '2rem' }}
            label={t('jobExperience.duration')}
            value={duration}
            onChange={(e) => {
              setDuration(e.target.value);
            }}
            type="number"
            variant="standard"
          />
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isLast}
                  onChange={(event) => {
                    setIsLast(event.target.checked);
                  }}
                />
              }
              label={t('jobExperience.isLast')}
            />
          </FormGroup>
          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}>
            <Button
              sx={{ color: 'gray', border: 1, borderColor: 'gray', fontWeight: 'normal' }}
              disabled={loading}
              onClick={() => onSubmit()}>
              {jobExperience ? t('jobExperience.edit') : t('jobExperience.add')}
            </Button>
            {jobExperience && (
              <Button
                sx={{ color: 'gray', border: 1, borderColor: 'gray', fontWeight: 'normal' }}
                disabled={loading}
                onClick={() => onDelete()}>
                {t('jobExperience.delete')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default JobExperienceModal;
